import type { Project, TicketDetail } from '@servicetim/shared';
import type { OptionLoader } from '@servicetim/ui/components';
import { createContext, useContext } from 'react';
import type { ProjectStatus, ProjectStatuses } from './api';
import type { TicketPatch } from './use-ticket-patch';

/** Everything the detail screen's sections share; assembled once in `TicketDetailScreen`. */
export interface TicketContextValue {
  ticket: TicketDetail;
  /** Route param the ticket was loaded under (id or key). */
  idOrKey: string;
  project: Project | undefined;
  /** `project.admin` here (admin status override, forms list). */
  canAdmin: boolean;
  statuses: ProjectStatuses;
  /** The ticket's current status when the published workflow is known. */
  status: ProjectStatus | undefined;
  meId: string;
  refetch: () => Promise<unknown>;
  patch: TicketPatch;
  patching: boolean;
  /** Best-effort label for a user id (display map, watchers, directory); null when unknown. */
  userLabel: (id: string | null | undefined) => string | null;
  /** Agent-safe people loader for the pickers. */
  loadUsers: OptionLoader;
}

const TicketContext = createContext<TicketContextValue | null>(null);

export const TicketProvider = TicketContext.Provider;

export function useTicketContext(): TicketContextValue {
  const value = useContext(TicketContext);
  if (value === null) throw new Error('useTicketContext() must be used inside <TicketProvider>');
  return value;
}
